<template>
  <VCardTitle class="d-flex justify-space-between items-center p-0">
    <div class="text-h4 p-4 pb-2">
      {{ t('Filter consultations') }}
    </div>
    <VBtn
      icon="close"
      variant="plain"
      class="button-small mr-2"
      size="small"
      :ripple="false"
      data-testid="btn-close"
      @click="emits('close-dialog')"
    />
  </VCardTitle>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Hospital') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.hospitals"
      clear-icon="custom:clear20"
      :items="hospitalData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Required Specialty') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.targetMedicalFields"
      clear-icon="custom:clear20"
      :items="targetMedicalFieldData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Patient Department') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.departments"
      clear-icon="custom:clear20"
      :items="patientDepartmentData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Consultation Type') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.urgencies"
      clear-icon="custom:clear20"
      :items="urgencyData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Assignee Name') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.assignees"
      clear-icon="custom:clear20"
      :items="assigneeData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
  <VCardItem class="p-4 pb-0">
    <span class="text-subtitle-1">
      {{ t('Requester Name') }}
    </span>
    <VAutocomplete
      v-model="consultationRequestStore.consultationFilterInput.requesters"
      clear-icon="custom:clear20"
      :items="requesterData"
      chips
      closable-chips
      multiple
      :placeholder="t('Please select...')"
    />
  </VCardItem>
</template>

<script setup lang="ts">
import type { HospitalDTO, UrgencyDTO, UserDTO } from '@/generated'
import { useConsultationRequestStore } from '@/stores/consultationRequest'
import type { SelectOption } from '@/types/SelectOption'
import { getTotalPatientCountForDepartmentName } from '@/util/getPatientCounts'
import { translateMedicalFieldsDTO, translateUrgencyDTO } from '@/util/translations'
import { userDTOtoDisplayName } from '@/util/userDTOtoDisplayName'
import { storeToRefs } from 'pinia'
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const emits = defineEmits(['close-dialog'])

const { t } = useI18n()
const consultationRequestStore = useConsultationRequestStore()
const {
  incomingDirectConsultations,
  outgoingDirectConsultations,
  incomingScheduledConsultations,
  outgoingScheduledConsultations
} = storeToRefs(consultationRequestStore)

const consultationRequests = computed(() => [
  ...incomingDirectConsultations.value,
  ...outgoingDirectConsultations.value
])

const scheduledConsultations = computed(() => [
  ...incomingScheduledConsultations.value,
  ...outgoingScheduledConsultations.value
])

const allCases = computed(() => [
  ...consultationRequests.value.map((c) => c.case),
  ...scheduledConsultations.value.flatMap((c) => c.cases)
])

onMounted(() => {
  consultationRequestStore.resetConsultationRequestFilterInput()
})

const hospitalData = computed<SelectOption<HospitalDTO>[]>(() => {
  const uniqueHospitals: HospitalDTO[] = [
    ...new Map(
      allCases.value.map((caze) => caze.hospital).map((hospital) => [hospital.id, hospital])
    ).values()
  ]

  return uniqueHospitals.map((hospital) => ({ value: hospital, title: hospital.name }))
})

const targetMedicalFieldData = computed<SelectOption<string>[]>(() => {
  const directMedicalFields = consultationRequests.value.map(
    (consultation) => consultation.medicalField
  )

  const scheduledMedicalFields = scheduledConsultations.value.flatMap((consultation) =>
    consultation.participants.flatMap((participant) => participant.medicalFields || [])
  )

  const uniqueMedicalFields = [...new Set([...directMedicalFields, ...scheduledMedicalFields])]

  return uniqueMedicalFields.map((medicalField: string) => {
    const directCount = consultationRequests.value.filter(
      (c) => c.medicalField === medicalField
    ).length
    const scheduledCount = scheduledConsultations.value.filter((c) =>
      c.participants.some((p) => p.medicalFields?.includes(medicalField))
    ).length
    const totalCount = directCount + scheduledCount

    return {
      value: medicalField,
      title: `${translateMedicalFieldsDTO(medicalField)} (${totalCount})`
    }
  })
})

const patientDepartmentData = computed<SelectOption<string>[]>(() => {
  const uniqueDepartmentNames = [...new Set(allCases.value.map((caze) => caze.department.name))]

  return uniqueDepartmentNames.map((departmentName) => {
    const count = getTotalPatientCountForDepartmentName(allCases.value, departmentName)

    return {
      value: departmentName,
      title: `${departmentName} (${count})`
    }
  })
})

const urgencyData = computed<SelectOption<UrgencyDTO>[]>(() => {
  const uniqueUrgencies: UrgencyDTO[] = [
    ...new Set(consultationRequests.value.map((consultation) => consultation.urgency))
  ]

  return uniqueUrgencies.map((urgency) => ({
    value: urgency,
    title: translateUrgencyDTO(urgency, t)
  }))
})

const assigneeData = computed<SelectOption<UserDTO>[]>(() => {
  const directAssignees = consultationRequests.value
    .map((consultation) => consultation.assignedTo)
    .filter((assignee) => !!assignee)

  const scheduledParticipants = scheduledConsultations.value.flatMap((consultation) =>
    consultation.participants.map((p) => p.user)
  )

  const uniqueAssignees: UserDTO[] = [
    ...new Map(
      [...directAssignees, ...scheduledParticipants].map((assignee) => [
        assignee.userName,
        assignee
      ])
    ).values()
  ]

  return uniqueAssignees.map((assignee) => ({
    value: assignee,
    title: userDTOtoDisplayName(assignee)
  }))
})

const requesterData = computed<SelectOption<UserDTO>[]>(() => {
  const directRequesters = consultationRequests.value.map(
    (consultation) => consultation.requestedBy.user
  )

  const scheduledRequesters = scheduledConsultations.value.map(
    (consultation) => consultation.requestedBy.user
  )

  const uniqueRequesters: UserDTO[] = [
    ...new Map(
      [...directRequesters, ...scheduledRequesters].map((requester) => [
        requester.userName,
        requester
      ])
    ).values()
  ]

  return uniqueRequesters.map((requester) => ({
    value: requester,
    title: userDTOtoDisplayName(requester)
  }))
})
</script>
